import { Box } from '@mui/material';
import { useEffect, useRef } from 'react';

import { env } from '@/config/env';

interface TurnstileApi {
  render: (el: HTMLElement, options: Record<string, unknown>) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise: Promise<TurnstileApi> | null = null;

/** Loads Cloudflare's script once per page; no npm dependency, so no licence question. */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () =>
        window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile missing'));
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('Turnstile failed to load'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

/** True when a site key is configured, i.e. the backend expects a CAPTCHA token. */
export const captchaEnabled = env.VITE_TURNSTILE_SITE_KEY !== '';

interface TurnstileProps {
  /** Must match what the backend checks for this endpoint: `login` or `signup`. */
  action: 'login' | 'signup';
  /** Called with a fresh token, or null when it expires or the challenge errors. */
  onToken: (token: string | null) => void;
  /** Change it to get a new widget: a token is single use, so remount after each attempt. */
  resetKey?: number;
}

/**
 * Cloudflare Turnstile CAPTCHA. Renders nothing when VITE_TURNSTILE_SITE_KEY is empty, which is
 * how local development without CAPTCHA works.
 */
export function Turnstile({ action, onToken, resetKey = 0 }: TurnstileProps) {
  const ref = useRef<HTMLDivElement>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!captchaEnabled || !ref.current) return undefined;
    const el = ref.current;
    let widgetId: string | null = null;
    let cancelled = false;
    onTokenRef.current(null);

    loadTurnstile()
      .then((api) => {
        if (cancelled) return;
        widgetId = api.render(el, {
          sitekey: env.VITE_TURNSTILE_SITE_KEY,
          action,
          callback: (token: string) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(null),
          'error-callback': () => onTokenRef.current(null),
        });
      })
      .catch(() => onTokenRef.current(null));

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [action, resetKey]);

  if (!captchaEnabled) return null;
  return <Box ref={ref} sx={{ minHeight: 65, display: 'flex', justifyContent: 'center' }} />;
}
